// React and hooks
import { memo, useCallback, useEffect, useState } from "react";

// API calls and types
import {
  getResidualValueAPI,
  getResidualValueTableAPI,
} from "services/api/calculations/residualvalue/api";
import {
  ResidualValueDetailFormValues,
  ResidualValueMonth,
  ResidualValueTable,
  ResidualValueTableValue,
} from "services/api/calculations/residualvalue/types";
import { ResidualValueDetailProps } from "./types";
import {
  initialResidualValueDetailForm,
  initialResidualValueTable,
} from "services/api/calculations/residualvalue/consts";
import { transformResidualValueTableValue } from "services/api/calculations/residualvalue/transformations";

// UI components
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import { Loader } from "ui/components/Loader";
import { TextField } from "ui/components/TextField/TextField";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Page specific components
import ResidualValueEditCard from "../../../ResidualValueEditCard/ResidualValueEditCard";

// Helpers and utilities
import { classNames, useTranslate } from "helpers";

// Constants
import { CALCULATION_RESIDUAL_VALUE_CREATE_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { Errors } from "services/forms/validation/types";



const ResidualValueDetailCard: React.FC<
  ResidualValueDetailProps
> = (props) => {
  const { residualValueId } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] =
    useState<ResidualValueDetailFormValues>(
      initialResidualValueDetailForm
    );

  const [residualValueTable, setResidualValueTable] =
    useState<ResidualValueTable>(initialResidualValueTable);

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (residualValueId) {
      fetchResidualValue(residualValueId);
      fetchResidualValueTable(residualValueId);
    }
  }, [residualValueId]);

  const fetchResidualValueTable = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialResidualValueTable);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getResidualValueTableAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setResidualValueTable(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchResidualValue = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialResidualValueDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getResidualValueAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (residualValueId) {
      fetchResidualValueTable(residualValueId);
      fetchResidualValue(residualValueId);
    }
    setShowEditModal(false);
  }, [fetchResidualValue, fetchResidualValueTable]);

  const renderPercentageTextbox = (
    kmId: string,
    monthId: string,
    values: ResidualValueTableValue[]
  ) => {
    let valuePercentage = transformResidualValueTableValue(
      kmId,
      monthId,
      values
    );

    return (
      <TextField
        type="number"
        name="value"
        onChange={null}
        disabled
        value={valuePercentage}
        groupAppend="%"
        cssClassName={classNames(
          "!ring-0 !rounded-none",
          valuePercentage > 0 ? "font-bold !text-red-500" : ""
        )}
      ></TextField>
    );
  };

  if (!residualValueId) return <></>;

  return (
    <>
      <div className="flex gap-4 ">
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
                {t("admin.calculation.setting.residualvalue.detail.title")}
              </p>
              <div className="flex items-center gap-2 px-3 my-3 ">
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.change")}
                  onClick={() => setShowEditModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>

            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
              {t("admin.calculation.setting.residualvalue.detail.name")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.name}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full ">
          <div className="bg-white">
            <table className="table-auto border-collapse border border-slate-200 w-full">
              <thead>
                <tr>
                  <th className="border border-slate-200 p-2 bg-slate-100 font-bold text-center text-sm  text-slate-900 uppercase min-w-[200px]">
                    {t(
                      "admin.calculation.setting.residualvalue.add.table.header"
                    )}
                  </th>
                  {residualValueTable.kms &&
                    residualValueTable.kms.map((item, index) => (
                      <th
                        key={index}
                        className="border border-slate-200 p-2 bg-white  px-4 py-3 text-xs font-normal text-center text-slate-900 uppercase"
                      >
                        {item.kms}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {residualValueTable.months &&
                  residualValueTable.months.map(
                    (monthItem: ResidualValueMonth) => (
                      <tr key={monthItem.id}>
                        <td className="border border-slate-200 p-2 bg-white text-center px-4 py-3 text-sm font-medium ">
                          {monthItem.months}
                        </td>
                        {residualValueTable.kms &&
                          residualValueTable.kms.map((kmItem, index) => (
                            <td
                              key={index}
                              className="border border-slate-200  bg-white text-center  text-sm font-medium"
                            >
                              {renderPercentageTextbox(
                                kmItem.id,
                                monthItem.id,
                                residualValueTable.values
                              )}
                            </td>
                          ))}
                      </tr>
                    )
                  )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size="full"
        >
          <ModalHeader
            title={CALCULATION_RESIDUAL_VALUE_CREATE_PAGE_TITLE.text}
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <ResidualValueEditCard
              residualValueId={residualValueId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(ResidualValueDetailCard);
